import { concat } from '@ember/helper';
import { htmlSafe, type SafeString } from '@ember/template';
import t from 'ember-intl/helpers/t';
import { and, eq, gt, not, or } from 'ember-truth-helpers';
import BvText from 'volta/components/bv-text';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import colorVar from 'volta/helpers/color-var';
import isPresent from 'volta/helpers/is-present';
import orElse from 'volta/helpers/or-else';
import cssVars from 'volta/modifiers/css-vars';
import { lower } from 'volta/utils/text-utils';

import type { TOC } from '@ember/component/template-only';
import type { TPOSyncStatus } from 'volta/models/types/planning-order';
import type { THueVariationString } from 'volta/utils/style-utils';
import type { BvTooltipSignature } from './../bv-tooltip';

export interface BvStatusSignature {
  Args: {
    abbreviated?: boolean;
    class?: string;
    color?: THueVariationString;
    isDone?: boolean;
    isError?: boolean;
    minimized?: boolean;
    progress?: number;
    status?: string | SafeString;
    style?: string;
    temporary?: boolean;
    syncStatus?: TPOSyncStatus;
    tooltip?: BvTooltipSignature['Args']['text'];
  };
  Blocks: {
    default: [];
  };
  Element: HTMLSpanElement;
}

const BvStatusComponent: TOC<BvStatusSignature> = <template>
  {{#let
    (or (eq @progress 100) @isDone)
    (if @color (colorVar @color variant='4'))
    (if @color (colorVar @color variant='3'))
    as |isDone darkColor lightColor|
  }}
    {{#let (and (gt @progress 0) (not isDone)) as |hasProgress|}}
      <span
        class={{classNames
          'bv-status'
          (if @abbreviated 'bv-status--abbreviated')
          (if @minimized 'bv-status--minimized')
          (if isDone 'bv-status--is-done')
          (if @temporary 'bv-status--temporary')
          (if @syncStatus (concat 'bv-status--sync-' (lower @syncStatus)))
          @class
        }}
        title={{@status}}
        style={{if @style (htmlSafe @style)}}
        ...attributes
        {{cssVars
          bv-status-bg-dark=darkColor
          bv-status-bg-light=lightColor
          bv-status-progress=(if hasProgress (concat '' @progress))
        }}
      >
        {{#unless @abbreviated}}
          <span class='bv-status__icon'>
            {{#if hasProgress}}
              <span class='bv-status__pie'></span>
            {{else if isDone}}
              <BvText @text='✓' />
            {{else if @isError}}
              <BvText @text='×' />
            {{else}}
              <span class='bv-status__dot'></span>
            {{/if}}
          </span>
        {{/unless}}
        {{#if @status}}
          {{#if @minimized}}
            <span class='u-visually-hidden'>
              {{@status}}
            </span>
          {{else if @abbreviated}}
            {{Abbreviate @status}}
          {{else}}
            {{@status}}
          {{/if}}
        {{else}}
          {{yield}}
        {{/if}}
        {{#if @syncStatus}}
          <BvTooltip @text={{t (concat 'syncStatuses.' @syncStatus)}} @subtext={{@tooltip}} />
        {{else if
          (and (or @abbreviated @minimized @syncStatus) (isPresent (orElse @tooltip @status)))
        }}
          <BvTooltip @text={{orElse @tooltip @status}} />
        {{/if}}
      </span>
    {{/let}}
  {{/let}}
</template>;

const Abbreviate = (str: string | SafeString) => str.toString()[0];

export default BvStatusComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvStatus: typeof BvStatusComponent;
    'bv-status': typeof BvStatusComponent;
  }
}
